/**
 * Financial Period Service
 * Handles all financial period related operations
 */

import { apiRequest } from '../utils/apiResponseHandler';
import { getApiUrl } from '../config/api';

const financialPeriodService = {
  // ==================== FINANCIAL PERIOD MANAGEMENT ====================

  // List Financial Periods
  async getFinancialPeriods(filters = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });
    
    const url = queryParams.toString() 
      ? `${getApiUrl('FINANCIAL_PERIODS')}?${queryParams.toString()}`
      : getApiUrl('FINANCIAL_PERIODS');
    
    return await apiRequest(url, {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Get Financial Period by ID
  async getFinancialPeriodById(periodId) {
    return await apiRequest(getApiUrl('FINANCIAL_PERIOD_BY_ID', periodId), {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Create Financial Period
  async createFinancialPeriod(periodData) {
    return await apiRequest(getApiUrl('FINANCIAL_PERIODS'), {
      method: 'POST',
      body: periodData,
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Update Financial Period
  async updateFinancialPeriod(periodId, periodData) {
    return await apiRequest(getApiUrl('FINANCIAL_PERIOD_BY_ID', periodId), {
      method: 'PUT',
      body: periodData,
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Partially Update Financial Period
  async patchFinancialPeriod(periodId, periodData) {
    return await apiRequest(getApiUrl('FINANCIAL_PERIOD_BY_ID', periodId), {
      method: 'PATCH',
      body: periodData,
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Delete Financial Period
  async deleteFinancialPeriod(periodId) {
    return await apiRequest(getApiUrl('FINANCIAL_PERIOD_BY_ID', periodId), {
      method: 'DELETE',
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // ==================== UTILITY FUNCTIONS ====================

  // Validate Period Data
  validatePeriodData(periodData) {
    const errors = {};
    
    if (!periodData.name?.trim()) {
      errors.name = 'Period name is required';
    }
    
    if (!periodData.start_date) {
      errors.start_date = 'Start date is required';
    }
    
    if (!periodData.end_date) {
      errors.end_date = 'End date is required';
    }
    
    if (periodData.start_date && periodData.end_date) {
      const startDate = new Date(periodData.start_date);
      const endDate = new Date(periodData.end_date);
      
      if (startDate >= endDate) {
        errors.end_date = 'End date must be after start date';
      }
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  },

  // Format Period Data for API
  formatPeriodDataForAPI(periodData) {
    return {
      name: periodData.name?.trim(),
      start_date: periodData.start_date,
      end_date: periodData.end_date,
      is_active: periodData.is_active !== false
    };
  },

  // Get Period Status Badge
  getPeriodStatusBadge(period) {
    const now = new Date();
    const startDate = new Date(period.start_date);
    const endDate = new Date(period.end_date);
    
    if (!period.is_active) {
      return { text: 'Inactive', class: 'inactive', color: '#e53e3e' };
    }
    
    if (now < startDate) {
      return { text: 'Upcoming', class: 'upcoming', color: '#3182ce' };
    }
    
    if (now >= startDate && now <= endDate) {
      return { text: 'Active', class: 'active', color: '#38a169' };
    }
    
    if (now > endDate) {
      return { text: 'Completed', class: 'completed', color: '#718096' };
    }
    
    return { text: 'Unknown', class: 'unknown', color: '#718096' };
  },

  // Check for Overlapping Periods
  checkForOverlaps(periodData, existingPeriods) {
    const startDate = new Date(periodData.start_date);
    const endDate = new Date(periodData.end_date);
    
    return existingPeriods.filter(period => {
      if (period.id === periodData.id) return false; // Exclude current period when updating
      
      const existingStart = new Date(period.start_date);
      const existingEnd = new Date(period.end_date);
      
      return (startDate <= existingEnd && endDate >= existingStart);
    });
  },

  // Get Current Active Period
  getCurrentActivePeriod(periods) {
    const now = new Date();
    return periods.find(period => {
      if (!period.is_active) return false;
      
      const startDate = new Date(period.start_date);
      const endDate = new Date(period.end_date);
      
      return now >= startDate && now <= endDate;
    });
  },

  // Get Upcoming Periods
  getUpcomingPeriods(periods) {
    const now = new Date();
    return periods.filter(period => {
      if (!period.is_active) return false;
      
      const startDate = new Date(period.start_date);
      return startDate > now;
    }).sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
  },

  // Get Completed Periods
  getCompletedPeriods(periods) {
    const now = new Date();
    return periods.filter(period => {
      const endDate = new Date(period.end_date);
      return endDate < now;
    }).sort((a, b) => new Date(b.end_date) - new Date(a.end_date));
  }
};

export { financialPeriodService };
export default financialPeriodService;
